import type { AlarmRow } from '../api/alarms'
import { escapeHtml } from '../inventoree/format'
import type { TableColumn } from '../ui/DataTable'

/**
 * Coloanele listei de alarme, cu HTML-ul din ProactiviteeAlarmsModel
 * (public/js/proactivitee.js), celula cu celula. Numele de ordonare sunt
 * cele din Alarms::jsonData; a 5-a coloana nu se ordoneaza pe server.
 */

export interface AlarmColumn extends TableColumn {
  name?: string
}

/** temperatureConvert(value, true) din general.js: C, sau F daca furnizorul asa a ales. */
export function temperatureHtml(value: number | string | null, unit: 'C' | 'F'): string {
  let out = escapeHtml(String(value ?? '')) + '<sup>o</sup>C'
  if (unit === 'F') {
    out = String(Math.round(parseInt(String(value ?? ''), 10) * 9 / 5 + 32)) + '<sup>o</sup>F'
  }
  return out
}

/**
 * convert_values() din conversion_helper.php, pentru valoarea unei alarme
 * active: temperatura doar cand semnul chiar o spune (gol, C sau F, oricum
 * scris), altfel valoarea cu semnul ei, cum e.
 */
export function convertValues(value: number | string | null, unit: 'C' | 'F', sign: string | null): string {
  if (value === null || value === '') return ''
  const letters = String(sign ?? '').replace(/[^A-Za-z]/g, '').toUpperCase()
  const isTemperature = sign === null || sign === '' || letters === 'C' || letters === 'F'
  if (!isTemperature) return escapeHtml(String(value)) + escapeHtml(sign ?? '')
  const shown = unit === 'F' ? String(Math.round((Number(value) * 9) / 5 + 32)) : escapeHtml(String(value))
  return shown + '<sup>o</sup>' + unit
}

const MINUS = '<i class="fas fa-minus font-10"></i>'

export function severityHtml(flag: number | string): string {
  switch (String(flag)) {
    case '0':
      return '<center><i data-toggle="tooltip" data-placement="left" title="Info" class="fas font-16 color-blue fa-info-circle"></i></center>'
    case '1':
      return '<center><i data-toggle="tooltip" data-placement="left" title="Minor" class="fas font-16 color-green2 fa-exclamation-triangle"></i></center>'
    case '2':
      return '<center><i data-toggle="tooltip" data-placement="left" title="Major" class="fas font-16 color-yellow fa-exclamation-triangle"></i></center>'
    case '3':
      return '<center><i data-toggle="tooltip" data-placement="left" title="Critical" class="fas font-16 color-red fa-exclamation-circle"></i></center>'
  }
  /* O gravitate lipsa nu darama tabelul. */
  return '<center>' + MINUS + '</center>'
}

export function valuesHtml(r: AlarmRow, unit: 'C' | 'F'): string {
  if (String(r.dinamic) === '1') {
    if (r.convert === 'TEMP') {
      return temperatureHtml(r.from_value, unit) + ' - ' + temperatureHtml(r.to_value, unit)
    }
    const sign = escapeHtml(r.sign ?? '')
    return escapeHtml(String(r.from_value ?? '')) + sign + ' - ' + escapeHtml(String(r.to_value ?? '')) + sign
  }
  return MINUS
}

export function alarmColumns(unit: 'C' | 'F'): AlarmColumn[] {
  const rows = (fn: (r: AlarmRow) => string) => (row: Record<string, unknown>) => fn(row as unknown as AlarmRow)
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      name: 'ID',
      html: rows((r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${r.ID}"><i class="fas fa-plus-circle"></i></center>`),
    },
    { index: 1, title: 'Severity', orderable: true, visible: true, name: 'flag', html: rows((r) => severityHtml(r.flag)) },
    { index: 2, title: 'Alarm', orderable: true, visible: true, name: 'type_name', html: rows((r) => escapeHtml(r.type_name ?? '')) },
    { index: 3, title: 'Category', orderable: true, visible: true, name: 'category_name', html: rows((r) => escapeHtml(r.category_name ?? '')) },
    { index: 4, title: 'Alarm values', orderable: true, visible: true, html: rows((r) => valuesHtml(r, unit)) },
    {
      index: 5,
      title: 'Active alarms',
      orderable: true,
      visible: true,
      name: 'affected_devices',
      html: rows((r) => `<label class="label label-danger">${Number(r.affected_devices) || 0}</label>`),
    },
    {
      index: 6,
      title: 'Status',
      orderable: true,
      visible: true,
      width: 100,
      name: 'status',
      html: rows((r) =>
        String(r.status) === '1'
          ? '<center><i class="fas font-16 color-green2 fa-check-square"></i></center>'
          : '<center><i class="fas font-16 color-red fa-minus-square"></i></center>',
      ),
    },
  ]
}
